import { isPromise, isString } from 'remeda'
import { focusWhenReady } from './autofocus'
import { type Cleanup, composeCleanup } from './cleanup'
import { bindLiveProps, type LivePropValues } from './live-props'

// Private types ---------------------------------------------------------------

type DraftInputElement = HTMLElement & {
  value: string | null
  readonly updateComplete: Promise<unknown>
}

// Public functions ------------------------------------------------------------

// Narrows the hook host to a scalar Web Awesome input API.
export const isDraftInputElement = (
  element: HTMLElement
): element is DraftInputElement =>
  'value' in element &&
  (isString(element.value) || element.value === null) &&
  'updateComplete' in element &&
  isPromise(element.updateComplete)

// Private functions -----------------------------------------------------------

// Narrows the component's form carrier to a hidden native input.
const isHiddenInput = (element: Element | null): element is HTMLInputElement =>
  element instanceof HTMLInputElement && element.type === 'hidden'

// Mirrors the visible component value into its native form carrier.
const syncFormValue = (element: DraftInputElement): void => {
  const hiddenInput = element.querySelector(
    '[data-form-value], [data-number-input-value]'
  )

  if (isHiddenInput(hiddenInput)) {
    hiddenInput.value = element.value ?? ''
  }
}

// Uses the named carrier as the LiveView event target without submitting twice.
const relayFormEvent = (
  element: DraftInputElement,
  event: Event,
  type: 'input' | 'change'
): void => {
  if (event.target !== element) {
    return
  }

  event.stopPropagation()
  syncFormValue(element)
  const carrier = element.querySelector(
    '[data-form-value], [data-number-input-value]'
  )

  if (isHiddenInput(carrier) && !carrier.disabled) {
    carrier.dispatchEvent(
      type === 'input' && event instanceof window.InputEvent
        ? new window.InputEvent(type, {
            bubbles: true,
            composed: true,
            data: event.data,
            inputType: event.inputType,
            isComposing: event.isComposing
          })
        : new Event(type, { bubbles: true })
    )
  }
}

// Flushes blur-debounced carrier events when the visible control loses focus.
const relayBlur = (element: DraftInputElement): void => {
  const carrier = element.querySelector(
    '[data-form-value], [data-number-input-value]'
  )

  if (isHiddenInput(carrier)) {
    carrier.dispatchEvent(new Event('blur'))
  }
}

// Reads the composite value for generic draft reconciliation.
const readValue = (element: DraftInputElement): LivePropValues => ({
  value: element.value
})

// Applies one server value and keeps the hidden input in the same transaction.
const applyValue = async (
  element: DraftInputElement,
  values: LivePropValues,
  signal: AbortSignal
): Promise<void> => {
  const value = values.value

  if (!(isString(value) || value === null)) {
    return
  }

  if (element.value !== value) {
    element.value = value
    await element.updateComplete
  }

  if (!signal.aborted) {
    syncFormValue(element)
  }
}

// Public functions ------------------------------------------------------------

// Connects value synchronization and returns its complete cleanup.
export const setupDraftInput = (element: HTMLElement): Cleanup => {
  if (
    !(
      isDraftInputElement(element) &&
      element.querySelector('[data-form-value], [data-number-input-value]')
    )
  ) {
    return () => undefined
  }

  const handleInput = (event: Event): void =>
    relayFormEvent(element, event, 'input')
  const handleChange = (event: Event): void =>
    relayFormEvent(element, event, 'change')
  const handleBlur = (): void => relayBlur(element)
  const cleanupLiveProps = bindLiveProps(element, {
    read: () => readValue(element),
    apply: (values, signal) => applyValue(element, values, signal)
  })

  focusWhenReady(element, element.updateComplete)
  element.addEventListener('input', handleInput)
  element.addEventListener('change', handleChange)
  element.addEventListener('focusout', handleBlur)

  return composeCleanup(cleanupLiveProps, () => {
    element.removeEventListener('input', handleInput)
    element.removeEventListener('change', handleChange)
    element.removeEventListener('focusout', handleBlur)
  })
}
